import React from "react";
import styled from "styled-components";
import axios from "axios";
import moment from "moment/moment";

const DateCell = styled.div`
  min-width: 60px;
  cursor: ${({ isPass }) => (isPass ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
`;

const HorizontalCalendar = (props) => {
  const { REACT_APP_BASE_API_URL } = process.env;
  const {
    onChange,
    pageColor,
    showLabel = true,
    shopId,
    selectedDate,
    workingHoursOfWeek,
  } = props;
  const [calendarData, setCalendarData] = React.useState([]);
  const [fullBookingDate, setFullBookingDate] = React.useState([]);
  const scrollContainerRef = React.useRef(null);

  React.useEffect(() => {
    getFullBookingDate();
  }, []);

  // Japanese day names
  const dayNames = ["月", "火", "水", "木", "金", "土", "日"];
  const dayClasses = [
    "text-dark",
    "text-dark",
    "text-dark",
    "text-dark",
    "text-dark",
    "text-primary",
    "text-danger",
  ];

  const getFullBookingDate = () => {
    axios({
      url: `${REACT_APP_BASE_API_URL}fully-booked-dates`,
      method: "GET",
      params: {
        shop_id: shopId,
      },
      headers: {
        Authorization: "5e83ca61808625c8e154986105ef2fb825f9f6c1",
      },
    }).then((result) => {
      setFullBookingDate(
        result.data?.fully_booked_dates.map((item) => item.date)
      );
    });
  };

  const getDayOfWeek = (dateStr) => {
    const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    const date = new Date(dateStr);
    return days[date.getDay()];
  };

  const isDayOff = (date) => {
    const workingDays = workingHoursOfWeek.map((d) => d.dayOfWeek);
    const dayOfWeek = getDayOfWeek(date);
    return !workingDays.includes(dayOfWeek);
  };

  // Generate calendar data for current month + 2 future months
  const generateCalendarData = React.useCallback(() => {
    const today = new Date();
    const data = [];
    for (let monthOffset = 0; monthOffset < 3; monthOffset++) {
      const targetDate = new Date(
        today.getFullYear(),
        today.getMonth() + monthOffset,
        1
      );
      const year = targetDate.getFullYear();
      const month = targetDate.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(year, month, day);
        const dayOfWeek =
          currentDate.getDay() === 0 ? 6 : currentDate.getDay() - 1; // Convert to Mon=0, Sun=6
        // Sample booking states (you can modify this logic)
        const isFullyBooked = !!fullBookingDate.includes(
          moment(currentDate).format("YYYY-MM-DD")
        ); // 10% chance of being fully booked
        const isClosed = isDayOff(moment(currentDate).format("YYYY-MM-DD")); // 30% chance Sunday is closed
        const isPrevDate =
          currentDate < today &&
          currentDate.toDateString() !== today.toDateString();

        data.push({
          date: day,
          month: month + 1,
          year,
          isToday: currentDate.toDateString() === today.toDateString(),
          isPast: isPrevDate || isClosed || isFullyBooked,
          isSelected: false,
          isFullyBooked,
          isClosed,
          dayOfWeek,
        });
      }
    }

    return data;
  }, [fullBookingDate]);

  React.useEffect(() => {
    const data = generateCalendarData();
    let index = data.findIndex((d) => d.isToday);
    if (selectedDate?.date) {
      index = data.findIndex(
        (d) => d.month === selectedDate.month && d.date === selectedDate.date
      );
    }
    if (index !== -1) {
      if (data[index].isClosed) {
        index++;
      }
      if (!selectedDate?.date) {
        onChange(data[index]);
      }

      data[index].isSelected = true;
      setTimeout(() => {
        scrollToCenter(index);
      }, 100);
    }

    setCalendarData(data);
  }, [fullBookingDate]);

  const handleDateClick = (clickedDate, index) => {
    if (clickedDate.isPast) return;

    const newData = calendarData.map((date, i) => ({
      ...date,
      isSelected: i === index,
    }));

    setCalendarData(newData);
    onChange?.(clickedDate);

    // Center the selected date
    scrollToCenter(index);
  };

  const scrollToWeek = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 280; // Approximate width of one week
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const scrollToCenter = (dateIndex) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const dateWidth = 60; // width of each date cell in Bootstrap
      const containerWidth = container.clientWidth;
      const targetPosition =
        dateIndex * dateWidth - containerWidth / 2 + dateWidth / 2;

      container.scrollTo({
        left: Math.max(0, targetPosition),
        behavior: "smooth",
      });
    }
  };

  const renderDate = (isFirstOfMonth, dateInfo) => {
    if (isFirstOfMonth) {
      return (
        <div className="small fw-bold">
          {dateInfo.month}/{dateInfo.date}
        </div>
      );
    }
    return <div className="small fw-bold">{dateInfo.date}</div>;
  };

  const renderStatusIndicator = (dateInfo, color, dayClass) => {
    if (dateInfo.isClosed) {
      return <span className={`${dayClass} fw-bold`}>休</span>;
    }
    if (dateInfo.isFullyBooked) {
      return (
        <span
          className="text-secondary"
          style={{
            color,
          }}
        >
          -
        </span>
      );
    }
    return (
      <div
        style={{
          border: `1px solid ${dateInfo.isSelected ? "white" : pageColor}`,
          width: "15px",
          height: "15px",
          borderRadius: "100%",
          flex: "none",
        }}
      />
    );
  };

  const renderDateCell = (dateInfo, index) => {
    const isFirstOfMonth = dateInfo.date === 1;
    const backgroundColor = dateInfo.isSelected ? pageColor : "transparent";
    const color = dateInfo.isSelected ? "white" : "#333";
    // Get day name and class
    const dayName = dayNames[dateInfo.dayOfWeek];
    const dayClass = dateInfo.isSelected
      ? "white"
      : dayClasses[dateInfo.dayOfWeek];

    let cellClasses = "text-center date-cell";
    if (dateInfo.isPast) {
      cellClasses += " opacity-50";
    } else {
      cellClasses += " date-cell-active";
    }

    return (
      <DateCell
        key={`${dateInfo.year}-${dateInfo.month}-${dateInfo.date}`}
        className={cellClasses}
        isPass={dateInfo.isPass}
        onClick={() => handleDateClick(dateInfo, index)}
      >
        {/* Day of week */}
        <div
          className={`small fw-medium pb-2 ${dayClass}`}
          style={{
            borderBottom: "1px solid #ddd",
          }}
        >
          {dayName}
        </div>
        <div
          className={`${!dateInfo.isSelected ? "date-cell-hover" : ""} pb-2`}
          style={{
            borderRight: "1px solid #ddd",
            backgroundColor,
            color,
          }}
        >
          {/* Date display */}
          <div className="text-center py-2">
            {renderDate(isFirstOfMonth, dateInfo)}
          </div>

          {/* Status indicator */}
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ width: "16px", height: "16px", margin: "0px auto" }}
          >
            {renderStatusIndicator(dateInfo, color, dayClass)}
          </div>
        </div>
      </DateCell>
    );
  };

  return (
    <div className="container-fluid p-3 bg-white">
      {/* Header */}
      {showLabel && (
        <div className="mb-3">
          <h2 className="fs-6 fw-medium text-dark">来店日</h2>
        </div>
      )}

      {/* Calendar scroll container */}
      <div className="position-relative border-bottom">
        <button
          className="btn btn-hover btn-sm btn-light position-absolute rounded start-0 top-50 translate-middle-y shadow-sm z-3"
          onClick={() => scrollToWeek("left")}
        >
          <i className="fas fa-solid fa-chevron-left"></i>
        </button>

        <div
          ref={scrollContainerRef}
          className="overflow-auto mx-4 calendar-container"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          <div className="d-flex">
            {calendarData.map((dateInfo, index) =>
              renderDateCell(dateInfo, index)
            )}
          </div>
        </div>

        <button
          className="btn btn-hover btn-sm btn-light rounded position-absolute end-0 top-50 translate-middle-y shadow-sm z-3"
          onClick={() => scrollToWeek("right")}
        >
          <i className="fas fa-solid fa-chevron-right"></i>
        </button>
      </div>

      <style>{`
        .calendar-container::-webkit-scrollbar {
          display: none;
        }
        .date-cell-hover:hover {
          background-color: rgba(0, 0, 0, 0.05) !important;
        }
        .btn-hover:hover {
          background-color: ${pageColor};
        }
      `}</style>
    </div>
  );
};

export default React.memo(HorizontalCalendar);
